import { format } from 'date-fns';

import { resolveFieldKindFromChecks } from './form-io-traverse.util';

const stringifySubmissionObject = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const CELL_FORMATTERS = {
  boolean: (v) => (v ? 'Yes' : 'No'),
  string: String,
  number: String,
  object: stringifySubmissionObject,
};

/**
 * @param {unknown} value
 * @returns {string}
 */
export const formatSubmissionDataCell = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const formatter = CELL_FORMATTERS[typeof value];
  return formatter ? formatter(value) : '';
};

/**
 * Form.io `datetime` submission values (ISO strings, epoch, etc.) shown in grid/CSV as a fixed local-time string.
 * @param {unknown} value
 * @returns {string}
 */
const toValidDate = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatFormSubmissionDatetimeCell = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const d = toValidDate(value);
  if (d === null) {
    return formatSubmissionDataCell(value);
  }
  return format(d, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * @param {string} value
 * @returns {string}
 */
const escapeHtmlAttr = (value) =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;');

const SIGNATURE_URL_PREFIXES = ['http://', 'https://', 'data:'];

const extractSignatureUrlFromString = (raw) => {
  const trimmed = raw.trim();
  const hasAllowedPrefix = SIGNATURE_URL_PREFIXES.some((p) => trimmed.startsWith(p));
  return hasAllowedPrefix ? trimmed : null;
};

const objectUrlString = (raw) => {
  const url = /** @type {{ url?: unknown }} */ (raw).url;
  if (typeof url !== 'string' || url.trim() === '') {
    return null;
  }
  return url.trim();
};

const extractSignatureUrlFromObject = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  return objectUrlString(raw);
};

/**
 * Form.io signature (and similar file) submission values: string data URL or `{ url }`.
 * @param {unknown} raw
 * @returns {string | null}
 */
export const extractSignatureOrFileUrl = (raw) => {
  if (typeof raw === 'string') {
    return extractSignatureUrlFromString(raw);
  }
  return extractSignatureUrlFromObject(raw);
};

/**
 * @param {unknown} value
 * @param {string} currencyCode ISO 4217 (e.g. USD, EUR).
 * @returns {string}
 */
const coerceFiniteNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return Number(value);
  }
  return Number.NaN;
};

const formatNumberAsCurrency = (amount, currencyCode) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(amount);
  } catch {
    return String(amount);
  }
};

export const formatCurrencyCell = (value, currencyCode) => {
  const code = typeof currencyCode === 'string' && currencyCode.trim() !== '' ? currencyCode.trim() : 'USD';
  const n = coerceFiniteNumber(value);
  if (!Number.isFinite(n)) {
    return formatSubmissionDataCell(value);
  }
  return formatNumberAsCurrency(n, code);
};

/**
 * Form.io `selectboxes` stores `{ optionKey: boolean }`. Display only keys that are `true`.
 * @param {unknown} value
 * @returns {string}
 */
export const formatSelectboxCell = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }
  const entries = Object.keys(value).filter((k) => /** @type {Record<string, unknown>} */ (value)[k] === true);
  return entries.join(', ');
};

/**
 * Form.io `survey` stores `{ questionKey: selectedValue }` (string answers per question).
 * @param {unknown} value
 * @returns {string}
 */
const SURVEY_PRIMITIVE_FORMATTERS = {
  string: String,
  number: String,
  boolean: String,
  bigint: String,
  object: stringifySubmissionObject,
};

const formatSurveyPrimitive = (v) => {
  const formatter = SURVEY_PRIMITIVE_FORMATTERS[typeof v];
  return formatter ? formatter(v) : '';
};

const formatSurveyEntry = (key, v) => {
  if (v === null || v === undefined) {
    return `${key}: `;
  }
  return `${key}: ${formatSurveyPrimitive(v)}`;
};

export const formatSurveyCell = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }
  const record = /** @type {Record<string, unknown>} */ (value);
  return Object.keys(record)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => formatSurveyEntry(key, record[key]))
    .join(', ');
};

export const isDataImageSignatureHref = (href) =>
  typeof href === 'string' && href.startsWith('data:image/');

const formatSignatureForCsv = (value) => {
  const href = extractSignatureOrFileUrl(value);
  if (!href) {
    return '';
  }
  return isDataImageSignatureHref(href) ? '[Signature image]' : href;
};

const CSV_FIELD_KIND_CHECKS = [
  ['signature', (opts) => opts.isSignatureField],
  ['currency', (opts) => opts.currencyCode !== undefined && opts.currencyCode !== ''],
  ['selectbox', (opts) => opts.isSelectboxField],
  ['survey', (opts) => opts.isSurveyField],
  ['datetime', (opts) => opts.isDatetimeField],
];

const resolveCsvFieldKind = (opts) => resolveFieldKindFromChecks(CSV_FIELD_KIND_CHECKS, opts);

const CSV_FIELD_FORMATTERS = {
  signature: (value) => formatSignatureForCsv(value),
  currency: (value, opts) => formatCurrencyCell(value, opts.currencyCode),
  selectbox: (value) => formatSelectboxCell(value),
  survey: (value) => formatSurveyCell(value),
  datetime: (value) => formatFormSubmissionDatetimeCell(value),
  default: (value) => formatSubmissionDataCell(value),
};

/**
 * @param {unknown} value
 * @param {boolean | { isSignatureField?: boolean, currencyCode?: string, isSelectboxField?: boolean, isSurveyField?: boolean, isDatetimeField?: boolean }} [optionsOrLegacySignature]
 * @returns {string}
 */
export const formatSubmissionDataForCsv = (value, optionsOrLegacySignature = {}) => {
  const opts =
    typeof optionsOrLegacySignature === 'boolean'
      ? { isSignatureField: optionsOrLegacySignature }
      : optionsOrLegacySignature;
  const kind = resolveCsvFieldKind(opts);
  return CSV_FIELD_FORMATTERS[kind](value, opts);
};

/**
 * @param {unknown} raw
 * @returns {string}
 */
export const buildSignatureViewFileHtml = (raw) => {
  const href = extractSignatureOrFileUrl(raw);
  if (!href) {
    return '';
  }
  const escaped = escapeHtmlAttr(href);
  // Browsers block opening data: URLs in a new tab from <a target="_blank">; show inline instead.
  if (isDataImageSignatureHref(href)) {
    return `<div class="form-submission-signature"><img src="${escaped}" alt="Signature" style="max-height:120px;max-width:min(320px,100%);display:block;height:auto;width:auto;object-fit:contain" loading="lazy" /></div>`;
  }
  return `<a href="${escaped}" target="_blank" rel="noopener noreferrer">View file</a>`;
};
